import React, { useState, useCallback } from 'react';
import EdenredTypo from '@sharedComponent/typography';
import ImageComponent from '@sharedComponent/image-component';
import { focus_state_icons } from 'src/assets/icons/focus_state';
import ETypo from '../Typo';
import { formatAcceptedTypesForDisplay } from './fileTypeUtils';
import './edragger.scss';

const MultiUploadDragger = ({
	acceptedTypes = '.pdf,.doc,.docx,.jpg,.jpeg,.png',
	idleText1 = 'Drag and drop or click to upload',
	idleText2 = 'multiple files',
	onChange,
	onRemoveUpload,
	onDownload,
	className = '',
	fileFormat = '',
	fileMaxSize = '',
	previousFiles = [],
	currentFiles = null,
}) => {
	const unModifiablePreviousFiles = previousFiles.map(file => ({
		id: file.id,
		name: file.name,
		modifiable: false
	}));
	const [files, setFiles] = useState([]);
	const [dragActive, setDragActive] = useState(false);
	// Use currentFiles from parent if provided, otherwise use internal state
	const displayFiles = currentFiles !== null ? currentFiles : files;
	const allFiles = [...displayFiles, ...unModifiablePreviousFiles];

	const generateFileId = () => {
		const array = new Uint32Array(1);
		// eslint-disable-next-line no-undef
		globalThis.crypto.getRandomValues(array);
		return array[0].toString(36).substring(0, 7);
	}

	const handleFiles = useCallback((fileList) => {
		const newFiles = Array.from(fileList).map(file => ({
			id: generateFileId(),
			file: file,
			name: file.name,
			size: file.size,
			modifiable: true
		}));

		// Only update internal state if currentFiles is not provided (parent manages state)
		if (currentFiles === null) {
			setFiles(prevFiles => [...newFiles, ...prevFiles]);
		}

		if (onChange) {
			onChange([...newFiles, ...files]);
		}
	}, [files, onChange, currentFiles]);

	const handleDrag = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			handleFiles(e.dataTransfer.files);
		}
	};

	const handleChange = (e) => {
		e.preventDefault();
		if (e.target.files && e.target.files[0]) {
			handleFiles(e.target.files);
		}
	};

	const removeFile = (fileId) => {
		const updatedFiles = files.filter(f => f.id !== fileId);
		setFiles(updatedFiles);
		if (onRemoveUpload) {
			onRemoveUpload(fileId);
		}
	};


	return (
		<div className={`main-container-dragger multi-upload ${className}`}>
			<div className="dragger">
				<div
					className={`dragger-container ${dragActive ? 'drag-active' : ''}`}
					style={{
						border: `2px dashed ${dragActive ? '#1890ff' : '#eff1f5'}`,
						backgroundColor: dragActive ? '#f0f8ff' : 'transparent'
					}}
					onDragEnter={handleDrag}
					onDragLeave={handleDrag}
					onDragOver={handleDrag}
					onDrop={handleDrop}
				>
					<div className="imageuploadIndicator">
						<ImageComponent src={focus_state_icons.hrUpload} />
					</div>
					<div className="dragger_hint">
						<EdenredTypo className="dragger_text">{idleText1}</EdenredTypo>
						<EdenredTypo className="dragger_text">{idleText2}</EdenredTypo>
						{fileFormat && (
							<ETypo light b2 style={{ textAlign: 'center' }}>
								{fileFormat}
							</ETypo>
						)}
						{fileMaxSize && (
							<ETypo light b2 style={{ textAlign: 'center' }}>
								{fileMaxSize}
							</ETypo>
						)}
						{acceptedTypes && formatAcceptedTypesForDisplay(acceptedTypes) && (
							<ETypo light b3 style={{ textAlign: 'center' }}>
								Accepted formats: {formatAcceptedTypesForDisplay(acceptedTypes)}
							</ETypo>
						)}
					</div>
				</div>
				<input
					type="file"
					id="multi-filesystem"
					accept={acceptedTypes}
					className="file-input"
					multiple
					onChange={handleChange}
				/>
			</div>

			{allFiles.length > 0 && (
				<div className="uploaded-files-list">
					{allFiles.map(fileObj => (
						<div
							key={fileObj.id}
							className="uploaded-file-item"
						>
							<div className="file-status-icon">
								<ImageComponent
									src={fileObj.error ? focus_state_icons.NewDesignErro : focus_state_icons.NewDesignCheck}
								/>
							</div>
							<div className="file-info">
								<ETypo className="file-name" title={fileObj.name}>{fileObj.name}</ETypo>
								{fileObj.error && fileObj.errorMessage && (
									<ETypo b3>
										{fileObj.errorMessage}
									</ETypo>
								)}
								{!fileObj.error && fileObj.size && (
									<ETypo light b3 className="file-size">
										{(fileObj.size / 1024 / 1024).toFixed(2)} MB
									</ETypo>
								)}
							</div>
							<div className="file-actions">
								{fileObj.modifiable ? (
									<ImageComponent
										src={focus_state_icons.NewDelete}
										onClick={() => removeFile(fileObj.id)}
										className="deleteIcon"
									/>
								) : (
									onDownload && (
										<ImageComponent
											src={focus_state_icons.downloadAttachement}
											onClick={() => onDownload(fileObj.id, fileObj.name)}
											className="downloadIcon"
											style={{ cursor: 'pointer' }}
										/>
									)
								)}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default MultiUploadDragger;